import { Box } from '@twilio-paste/core/box';
import { FormControl, FormSection, FormSectionHeading } from '@twilio-paste/core/form';
import { HelpText } from '@twilio-paste/core/help-text';
import { Input } from '@twilio-paste/core/input';
import { Label } from '@twilio-paste/core/label';
import { Radio, RadioGroup } from '@twilio-paste/core/radio-group';
import { Separator } from '@twilio-paste/core/separator';
import { Stack } from '@twilio-paste/core/stack';
import { useUIDSeed } from '@twilio-paste/core/uid-library';
import { Field, useFormikContext } from 'formik';
import * as Yup from 'yup';

import { ForwardMethod, MessageActionType, useApp } from '../../../modules/Apps';
import { InfoLabel } from '../../common/InfoLabel';
import { ViewErrorState } from '../../common/ViewErrorState';
import { ViewLoadingState } from '../../common/ViewLoadingState';

import { SelectScopesDropDown } from './SelectScopesComboBox';
import { TargetAuthHeaderContextProvider } from './TargetAuthHeader/TargetAuthHeaderContext';
import { TargetAuthHeaderSection } from './TargetAuthHeader/TargetAuthHeaderSection';
import { TargetsFormValues } from './types';

const messageActionTypeDisplay: Record<MessageActionType, string> = {
  [MessageActionType.Append]: 'Append Message',
  [MessageActionType.None]: 'None',
  [MessageActionType.Prepend]: 'Prepend Message',
};

export const createTargetsFormValidationSchema = () => {
  return Yup.object().shape({
    appId: Yup.string().max(26, 'App id is too long').required('App is required'),
    name: Yup.string().max(100, 'Target name is too long').required('Target name is required'),
    forwardUrl: Yup.string()
      .url('It must be a valid url')
      .max(100, 'Forward Url is too long')
      .required('Forward Url is required'),
    forwardMethod: Yup.string()
      .oneOf(Object.values(ForwardMethod))
      .max(255, 'Forward Method is too long')
      .required('Forward Method is required'),
    scopes: Yup.array(),
    messageActionType: Yup.string()
      .oneOf(Object.values(MessageActionType))
      .required('Message action type is required'),
    message: Yup.string().when('messageActionType', {
      is: (value: MessageActionType) => value !== MessageActionType.None,
      then: schema => schema.required('Message is required.').max(100, 'Message is too long.'),
    }),
  });
};

interface TargetsFormProps {
  appId: string;
  readOnly?: boolean;
}

export function TargetsForm({ appId, readOnly }: TargetsFormProps) {
  const seed = useUIDSeed();
  const { touched, errors, values, setFieldValue } = useFormikContext<TargetsFormValues>();
  const { data: app, isError, isLoading } = useApp(appId);

  if (isLoading) {
    return <ViewLoadingState />;
  }

  if (!app || isError) {
    return <ViewErrorState />;
  }

  const { scopes = [] } = app;

  return (
    <Box maxWidth="600px">
      <Stack orientation="vertical" spacing="space60">
        <FormSection>
          <FormSectionHeading>App Details</FormSectionHeading>
          <FormControl>
            <InfoLabel
              htmlFor={seed('name')}
              infoText="Provide a friendly name to identify the application target."
              required
            >
              Target Name
            </InfoLabel>
            <Field
              name="name"
              as={Input}
              type="text"
              id={seed('name')}
              placeholder="Target Name"
              hasError={touched.name && !!errors.name}
              readOnly={readOnly}
            />
            {touched.name && !!errors.name && <HelpText variant="error">{errors.name}</HelpText>}
          </FormControl>
          <SelectScopesDropDown scopes={scopes} readOnly={readOnly} />
        </FormSection>
        <Separator orientation="horizontal" />
        <FormSection>
          <FormSectionHeading>SMS Configuration</FormSectionHeading>
          <FormControl>
            <RadioGroup
              id={seed('messageActionType')}
              name="messageActionType"
              legend={
                <InfoLabel
                  htmlFor={seed('messageActionType')}
                  infoText="Modify the message before sending it to the target application."
                >
                  Modify Message Actions
                </InfoLabel>
              }
              value={values.messageActionType}
              onChange={value => setFieldValue('messageActionType', value)}
              orientation="horizontal"
              disabled={readOnly}
            >
              {Object.values(MessageActionType).map(method => {
                return (
                  <Radio value={method} name="messageActionType" key={method} readOnly={readOnly}>
                    {messageActionTypeDisplay[method]}
                  </Radio>
                );
              })}
            </RadioGroup>
            {touched.messageActionType && !!errors.messageActionType && (
              <HelpText variant="error">{errors.messageActionType}</HelpText>
            )}
          </FormControl>

          {values.messageActionType !== MessageActionType.None && (
            <FormControl>
              <Label htmlFor={seed('message')} required>
                <MessageActionLabel type={values.messageActionType} htmlFor={seed('message')} />
              </Label>
              <Field
                name="message"
                as={Input}
                type="text"
                id={seed('message')}
                placeholder="Message"
                hasError={touched.message && !!errors.message}
                readOnly={readOnly}
              />
              {touched.message && !!errors.message && (
                <HelpText variant="error">{errors.message}</HelpText>
              )}
            </FormControl>
          )}
        </FormSection>
        <Separator orientation="horizontal" />
        <FormSection>
          <FormSectionHeading>Target Destination</FormSectionHeading>
          <FormControl>
            <InfoLabel
              htmlFor={seed('forwardUrl')}
              infoText="Provide the target application URL to receive forwarded requests from the application."
              required
            >
              Target Url
            </InfoLabel>
            <Field
              name="forwardUrl"
              as={Input}
              type="text"
              id={seed('forwardUrl')}
              placeholder="Forward Url"
              hasError={touched.forwardUrl && !!errors.forwardUrl}
              readOnly={readOnly}
            />
            {touched.forwardUrl && !!errors.forwardUrl && (
              <HelpText variant="error">{errors.forwardUrl}</HelpText>
            )}
          </FormControl>
          <FormControl>
            <RadioGroup
              id={seed('forwardMethod')}
              name="forwardMethod"
              legend={
                <InfoLabel
                  htmlFor={seed('forwardMethod')}
                  infoText="Provide the HTTP method used by the target application."
                >
                  Target HTTP Method
                </InfoLabel>
              }
              value={values.forwardMethod}
              onChange={value => setFieldValue('forwardMethod', value)}
              orientation="horizontal"
              disabled={readOnly}
            >
              {Object.values(ForwardMethod).map(method => {
                return (
                  <Radio value={method} name="forwardMethod" key={method} readOnly={readOnly}>
                    {method}
                  </Radio>
                );
              })}
            </RadioGroup>
            {touched.forwardMethod && !!errors.forwardMethod && (
              <HelpText variant="error">{errors.forwardMethod}</HelpText>
            )}
          </FormControl>
          <TargetAuthHeaderContextProvider readOnly={readOnly}>
            <TargetAuthHeaderSection />
          </TargetAuthHeaderContextProvider>
        </FormSection>
      </Stack>
    </Box>
  );
}

interface MessageActionLabelProps {
  type: MessageActionType;
  htmlFor: string;
}

function MessageActionLabel({ type, htmlFor }: MessageActionLabelProps) {
  if (type === MessageActionType.Append) {
    return (
      <InfoLabel
        htmlFor={htmlFor}
        infoText="Append text to the message before sending it to the target application."
      >
        Append To Message
      </InfoLabel>
    );
  }

  return (
    <InfoLabel
      htmlFor={htmlFor}
      infoText="Prepend text to the message before sending it to the target application."
    >
      Prepend To Message
    </InfoLabel>
  );
}
